import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, User, Mail, Lock, Upload, MapPin, Sparkles } from "lucide-react";
import { z } from "zod";
import { validatePassword, PASSWORD_REQUIREMENTS } from "@/lib/security";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { useTranslation } from "react-i18next";

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const profileSchema = z.object({
    display_name: z.string().trim().min(1, t("profile.errors.displayNameRequired")).max(100, t("profile.errors.displayNameTooLong")),
    headline: z.string().trim().max(120, t("profile.errors.headlineTooLong")).optional(),
    bio: z.string().trim().max(500, t("profile.errors.bioTooLong")).optional(),
    location: z.string().trim().max(120, t("profile.errors.locationTooLong")).optional(),
  });

  const passwordSchema = z.object({
    newPassword: z
      .string()
      .min(PASSWORD_REQUIREMENTS.minLength, t("profile.errors.passwordMinLength", { count: PASSWORD_REQUIREMENTS.minLength })),
    confirmPassword: z.string(),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t("profile.errors.passwordsMismatch"),
    path: ["confirmPassword"],
  });

  type ProfileSchema = z.infer<typeof profileSchema>;
  type PasswordSchema = z.infer<typeof passwordSchema>;

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileErrors, setProfileErrors] = useState<Partial<Record<keyof ProfileSchema, string>>>({});
  const [passwordErrors, setPasswordErrors] = useState<Partial<Record<keyof PasswordSchema, string>>>({});

  const loadProfile = useCallback(async () => {
    if (!user) return;

    setEmail(user.email || "");

    const { data, error } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      console.error("Error loading profile:", error);
      toast({
        title: t("profile.errors.loadTitle"),
        description: error.message || t("profile.errors.loadDesc"),
        variant: "destructive",
      });
      return;
    }

    if (data) {
      setDisplayName(data.display_name);
      setAvatarUrl(data.avatar_url);
      setHeadline(user.user_metadata?.headline || "");
      setBio(user.user_metadata?.bio || "");
      setLocation(user.user_metadata?.location || "");
      return;
    }

    const fallbackName =
      user.user_metadata?.display_name || user.email?.split("@")[0] || t("profile.fallbackName");

    const { data: newProfile, error: upsertError } = await supabase
      .from("profiles")
      .upsert(
        {
          user_id: user.id,
          display_name: fallbackName,
          avatar_url: user.user_metadata?.avatar_url || null,
        },
        { onConflict: "user_id" }
      )
      .select("display_name, avatar_url")
      .single();

    if (upsertError) {
      console.error("Error creating missing profile:", upsertError);
      return;
    }

    setDisplayName(newProfile.display_name);
    setAvatarUrl(newProfile.avatar_url);
    setHeadline(user.user_metadata?.headline || "");
    setBio(user.user_metadata?.bio || "");
    setLocation(user.user_metadata?.location || "");
  }, [toast, t, user]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/auth");
      return;
    }

    loadProfile();
  }, [authLoading, user, navigate, loadProfile]);

  const syncAuthMetadata = async (data: {
    display_name?: string;
    avatar_url?: string | null;
    headline?: string;
    bio?: string;
    location?: string;
  }) => {
    if (!user) return;

    const { error } = await supabase.auth.updateUser({
      data: {
        ...user.user_metadata,
        ...data,
      },
    });

    if (error) {
      console.error("Error syncing auth metadata:", error);
      toast({
        title: t("profile.warnings.title"),
        description: t("profile.warnings.sessionRefresh"),
        variant: "destructive",
      });
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = profileSchema.safeParse({ display_name: displayName, headline, bio, location });
    if (!validation.success) {
      const formattedErrors = validation.error.flatten().fieldErrors;
      setProfileErrors({
        display_name: formattedErrors.display_name?.[0],
        headline: formattedErrors.headline?.[0],
        bio: formattedErrors.bio?.[0],
        location: formattedErrors.location?.[0],
      });
      toast({
        title: t("profile.errors.validationTitle"),
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setProfileErrors({});
    setLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .upsert(
          { user_id: user!.id, display_name: validation.data.display_name, avatar_url: avatarUrl },
          { onConflict: "user_id" }
        );

      if (error) throw error;

      await syncAuthMetadata({
        display_name: validation.data.display_name,
        avatar_url: avatarUrl,
        headline: validation.data.headline,
        bio: validation.data.bio,
        location: validation.data.location,
      });

      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.warn("Session refresh failed after profile update:", refreshError);
      }

      await loadProfile();

      toast({
        title: t("profile.success.updatedTitle"),
        description: t("profile.success.updatedDesc"),
      });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("profile.errors.updateFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      toast({
        title: t("profile.errors.weakPasswordTitle"),
        description: passwordValidation.errors[0],
        variant: "destructive",
      });
      return;
    }

    const validation = passwordSchema.safeParse({ newPassword, confirmPassword });
    if (!validation.success) {
      const formattedErrors = validation.error.flatten().fieldErrors;
      setPasswordErrors({
        newPassword: formattedErrors.newPassword?.[0],
        confirmPassword: formattedErrors.confirmPassword?.[0],
      });
      toast({
        title: t("profile.errors.validationTitle"),
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setPasswordErrors({});
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: t("profile.success.passwordTitle"),
        description: t("profile.success.passwordDesc"),
      });

      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("profile.errors.passwordUpdateFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t("profile.errors.fileTooLargeTitle"),
        description: t("profile.errors.fileTooLargeDesc"),
        variant: "destructive",
      });
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast({
        title: t("profile.errors.fileTypeTitle"),
        description: t("profile.errors.fileTypeDesc"),
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("guest-avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("guest-avatars").getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      await syncAuthMetadata({ avatar_url: publicUrl });

      toast({
        title: t("profile.success.avatarTitle"),
        description: t("profile.success.avatarDesc"),
      });
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("profile.errors.avatarUpdateFailed"),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;
    setUploading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("user_id", user.id);

      if (error) throw error;

      setAvatarUrl(null);
      await syncAuthMetadata({ avatar_url: null });

      toast({
        title: t("profile.success.updatedTitle"),
        description: t("profile.success.avatarRemovedDesc", "Profile photo removed."),
      });
    } catch (error: any) {
      console.error("Error removing avatar:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("profile.errors.avatarUpdateFailed"),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getInitials = () => {
    if (!displayName) return "U";
    return displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const completionItems = [
    Boolean(displayName.trim()),
    Boolean(headline.trim()),
    Boolean(location.trim()),
    Boolean(bio.trim()),
    Boolean(avatarUrl),
  ];
  const profileCompletion = Math.round((completionItems.filter(Boolean).length / completionItems.length) * 100);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.08),transparent_35%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.35))]">
      <div className="container mx-auto max-w-6xl px-4 py-8 md:py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="rounded-full bg-background/80">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <p className="text-sm text-muted-foreground">{t("profile.breadcrumb")}</p>
              <h1 className="text-3xl font-semibold leading-tight">{t("profile.title")}</h1>
              <p className="mt-1 max-w-2xl text-muted-foreground">{t("profile.subtitle")}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Profile completion</p>
            <p className="mt-1 text-2xl font-semibold">{profileCompletion}%</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.5fr_0.9fr]">
          <div className="space-y-6">
            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <User className="h-5 w-5 text-primary" />
                  {t("profile.profileInfoTitle")}
                </CardTitle>
                <CardDescription>{t("profile.profileInfoDesc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-muted/30 p-4 sm:flex-row sm:items-center">
                    <Avatar className="h-20 w-20 ring-4 ring-background shadow-sm">
                      <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                      <AvatarFallback className="text-xl font-semibold">{getInitials()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label htmlFor="avatar">{t("profile.profilePhoto")}</Label>
                        <p className="text-xs text-muted-foreground">{t("profile.photoHint")}</p>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <Input
                          id="avatar"
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          disabled={uploading}
                          className="max-w-sm bg-background"
                        />
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" onClick={handleRemoveAvatar} disabled={uploading || !avatarUrl}>
                            {t("profile.remove")}
                          </Button>
                          <Button type="button" variant="secondary" disabled className="bg-primary/10 text-primary hover:bg-primary/10">
                            <Upload className="mr-2 h-4 w-4" />
                            {uploading ? t("profile.uploading") : t("profile.uploadPhoto")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="displayName">{t("profile.displayName")}</Label>
                      <Input
                        id="displayName"
                        type="text"
                        value={displayName}
                        onChange={(e) => {
                          setDisplayName(e.target.value);
                          setProfileErrors((prev) => ({ ...prev, display_name: undefined }));
                        }}
                        placeholder={t("profile.displayNamePlaceholder")}
                        maxLength={100}
                        aria-invalid={!!profileErrors.display_name}
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <p>{t("profile.displayNameHint")}</p>
                        <span>{displayName.length}/100</span>
                      </div>
                      {profileErrors.display_name && <p className="text-xs text-destructive">{profileErrors.display_name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {t("profile.email")}
                      </Label>
                      <Input id="email" type="email" value={email} disabled className="bg-muted" />
                      <p className="text-xs text-muted-foreground">{t("profile.emailHint")}</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="headline">{t("profile.headline")}</Label>
                      <Input
                        id="headline"
                        type="text"
                        value={headline}
                        onChange={(e) => {
                          setHeadline(e.target.value);
                          setProfileErrors((prev) => ({ ...prev, headline: undefined }));
                        }}
                        placeholder={t("profile.headlinePlaceholder")}
                        maxLength={120}
                        aria-invalid={!!profileErrors.headline}
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <p>{t("profile.headlineHint")}</p>
                        <span>{headline.length}/120</span>
                      </div>
                      {profileErrors.headline && <p className="text-xs text-destructive">{profileErrors.headline}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">{t("profile.location")}</Label>
                      <Input
                        id="location"
                        type="text"
                        value={location}
                        onChange={(e) => {
                          setLocation(e.target.value);
                          setProfileErrors((prev) => ({ ...prev, location: undefined }));
                        }}
                        placeholder={t("profile.locationPlaceholder")}
                        maxLength={120}
                        aria-invalid={!!profileErrors.location}
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <p>{t("profile.locationHint")}</p>
                        <span>{location.length}/120</span>
                      </div>
                      {profileErrors.location && <p className="text-xs text-destructive">{profileErrors.location}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">{t("profile.bio")}</Label>
                    <Textarea
                      id="bio"
                      value={bio}
                      onChange={(e) => {
                        setBio(e.target.value);
                        setProfileErrors((prev) => ({ ...prev, bio: undefined }));
                      }}
                      placeholder={t("profile.bioPlaceholder")}
                      rows={5}
                      maxLength={500}
                      aria-invalid={!!profileErrors.bio}
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <p>{t("profile.bioHint")}</p>
                      <span>{bio.length}/500</span>
                    </div>
                    {profileErrors.bio && <p className="text-xs text-destructive">{profileErrors.bio}</p>}
                  </div>

                  <div className="flex flex-col gap-3 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">{t("profile.profileSaveHint")}</p>
                    <Button type="submit" disabled={loading} className="sm:min-w-36">
                      {loading ? t("profile.updating") : t("profile.updateProfile")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Lock className="h-5 w-5 text-primary" />
                  {t("profile.changePasswordTitle")}
                </CardTitle>
                <CardDescription>{t("profile.changePasswordDescAlt")}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">{t("profile.newPassword")}</Label>
                    <PasswordInput
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordErrors((prev) => ({ ...prev, newPassword: undefined }));
                      }}
                      placeholder={t("profile.newPasswordPlaceholder")}
                      aria-invalid={!!passwordErrors.newPassword}
                    />
                    <PasswordStrengthIndicator password={newPassword} />
                    {passwordErrors.newPassword && <p className="text-xs text-destructive">{passwordErrors.newPassword}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t("profile.confirmPassword")}</Label>
                    <PasswordInput
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                      }}
                      placeholder={t("profile.confirmPasswordPlaceholder")}
                      aria-invalid={!!passwordErrors.confirmPassword}
                    />
                    {passwordErrors.confirmPassword && <p className="text-xs text-destructive">{passwordErrors.confirmPassword}</p>}
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={loading} className="sm:min-w-36">
                      {loading ? t("profile.updating") : t("profile.updatePassword")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl">{t("profile.previewTitle")}</CardTitle>
                <CardDescription>{t("profile.previewDescAlt")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="rounded-2xl border border-border/60 bg-[linear-gradient(135deg,hsl(var(--primary)/0.12),transparent_65%)] p-5">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-20 w-20 border-4 border-background shadow-sm">
                      <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                      <AvatarFallback className="text-xl">{getInitials()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-semibold leading-tight">{displayName || t("profile.previewNameFallback")}</h3>
                      <p className="truncate text-sm text-muted-foreground">{email || t("profile.previewEmailFallback")}</p>
                      {headline && <p className="mt-2 text-sm font-medium">{headline}</p>}
                    </div>
                  </div>
                  {(location || bio) && (
                    <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                      {location && (
                        <p className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {location}
                        </p>
                      )}
                      {bio && <p className="leading-6">{bio}</p>}
                    </div>
                  )}
                </div>

                <div className="space-y-3 rounded-2xl border border-dashed border-border bg-muted/20 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Ready-to-use checklist
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>{displayName ? "Display name is set." : "Add a clear display name for meetings."}</li>
                    <li>{headline ? "Headline added." : "Add a short headline so teammates know your role."}</li>
                    <li>{avatarUrl ? "Profile photo uploaded." : "Upload a photo to make guest meetings feel more personal."}</li>
                    <li>{bio ? "Bio looks good." : "A short bio helps on shared workspaces and recordings."}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
